import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import IconeEscola from '../assets/iconeescola.png';
import { 
  Target, 
  Clock, 
  Award, 
  TrendingUp, 
  User, 
  LogOut,
  PlayCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ArrowLeft,
  Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssessmentsList = () => {
  const { user, logout, token } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [trailStatusMap, setTrailStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const headerButtonBase =
    "border-0 text-white shadow-sm shadow-black/30 ring-1 ring-white/10 hover:text-white";
  const primaryButtonBase =
    "bg-amber-500/90 hover:bg-amber-400 text-white border-0 shadow-sm shadow-black/30 ring-1 ring-white/10";

  useEffect(() => {
    if (token) {
      fetchAssessments();
    }
  }, [token]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const [assessmentsResponse, trailsResponse, coursesResponse] = await Promise.all([
        axios.get(`${API}/assessments`, { headers }),
        axios.get(`${API}/user/trails`, { headers }),
        axios.get(`${API}/courses`, { headers }),
      ]);

      // Mapa de trilhas ativas do usuário: assessment_id → trail
      const trailMap = {};
      (trailsResponse.data || []).forEach(trail => {
        if (trail.linked_assessment_id) {
          trailMap[trail.linked_assessment_id] = trail;
        }
      });
      setTrailStatusMap(trailMap);

      // IDs dos cursos que o usuário tem acesso (courses retorna apenas os do setor/trilha do usuário)
      const accessibleCourseIds = new Set(
        (coursesResponse.data || []).map(c => c.id)
      );

      // IDs dos cursos que pertencem às trilhas ativas do usuário
      const trailCourseIds = new Set();
      (trailsResponse.data || []).forEach(trail => {
        (trail.course_ids || []).forEach(id => trailCourseIds.add(id));
      });

      const allAssessments = assessmentsResponse.data || [];
      const available = allAssessments.filter(assessment => {
        const linkedTrail = trailMap[assessment.id];

        // É avaliação final de trilha?
        const isTrailFinal = Boolean(
          assessment.is_trail_final_assessment ||
          (assessment.linked_trails && assessment.linked_trails.some(t => t.requires_assessment))
        );

        if (isTrailFinal) {
          // Só exibir se o usuário tiver essa trilha ativa E puder realizá-la
          if (!linkedTrail) return false;
          if (!linkedTrail.assessment_passed && !linkedTrail.can_take_final_assessment) return false;
          return true;
        }

        // Avaliação vinculada a um curso específico (course_id preenchido)
        if (assessment.course_id) {
          // Só mostrar se o curso está acessível ao usuário (na lista de cursos dele)
          return accessibleCourseIds.has(assessment.course_id) ||
                 trailCourseIds.has(assessment.course_id);
        }

        // Avaliação standalone (sem course_id e sem trilha): só mostrar se
        // não está vinculada a nenhuma trilha que o usuário não tem
        const hasAnyLinkedTrail = assessment.linked_trails && assessment.linked_trails.length > 0;
        if (hasAnyLinkedTrail) return false; // tem trilha mas não está no mapa do usuário

        return true;
      });
      setAssessments(available);
      
    } catch (error) {
      console.error("Error fetching assessments:", error);
      toast.error("Erro ao carregar avaliações");
    } finally {
      setLoading(false);
    }
  };

  const getSectorDisplayName = (sector) => {
    const sectorNames = {
      'estagio': 'Estágio',
      'tecnico': 'Técnico', 
      'comercial': 'Comercial'
    };
    return sectorNames[sector] || sector;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-300">Carregando avaliações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      {/* Header */}
      <header className="navbar">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src={IconeEscola} 
              alt="Escola de Suporti" 
              className="h-20 w-auto" 
            />
            <div>
              <h1 className="nav-brand text-white">Avaliações</h1>
              <p className="text-sm text-gray-400">Sistema de Avaliações</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-white">{user.name}</p>
              <p className="text-sm text-gray-400">
                {getSectorDisplayName(user.sector)} • {user.role}
              </p>
            </div>
            
            <Link to="/dashboard">
              <Button
                variant="outline"
                size="sm"
                className={`${headerButtonBase} bg-amber-500/90 hover:bg-amber-400`}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={logout}
              className={`${headerButtonBase} bg-rose-600/90 hover:bg-rose-500`}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Avaliações Disponíveis
          </h2>
          <p className="text-lg text-gray-300">
            Teste seus conhecimentos e obtenha certificações em <strong className="text-white">{getSectorDisplayName(user.sector)}</strong>.
          </p>
        </div>

        {/* Assessments Grid */}
        {assessments.length === 0 ? (
          <div className="text-center py-20">
            <Target className="w-24 h-24 text-gray-500 mx-auto mb-6 opacity-50" />
            <h3 className="text-2xl font-bold text-white mb-3">
              Nenhuma avaliação disponível
            </h3>
            <p className="text-gray-400 text-lg">
              No momento não há avaliações disponíveis para seu setor. 
              Entre em contato com o administrador.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessments.map((assessment) => {
              const linkedTrail = trailStatusMap[assessment.id];
              const isTrailFinalAssessment = Boolean(assessment.is_trail_final_assessment || linkedTrail);
              const assessmentButtonDisabled = Boolean(
                linkedTrail && !linkedTrail.assessment_passed && !linkedTrail.can_take_final_assessment
              );
              const startButtonLabel = linkedTrail
                ? (linkedTrail.assessment_passed ? "Ver avaliação final" : "Iniciar avaliação final")
                : "Iniciar Avaliação";
              const lockedReason = linkedTrail?.final_assessment_locked_reason;
              const linkedTrailNames = (assessment.linked_trails || []).map(t => t.trail_name).join(", ");

              return (
                <Card key={assessment.id} className="card bg-gray-900 border-gray-700 hover:border-gray-600 transition-all group" data-testid={`assessment-card-${assessment.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white group-hover:text-blue-400 transition-colors">
                          {assessment.title}
                        </CardTitle>
                      </div>
                    </div>
                  </div>
                  
                  <CardDescription className="text-gray-300 mt-3">
                    {assessment.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-300">
                        Questões: {assessment.question_count ?? 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">
                        {assessment.time_limit ? `${assessment.time_limit} min` : 'Sem limite'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-400" />
                      <span className="text-gray-300">Min: {assessment.passing_grade}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      <span className="text-gray-300">{assessment.max_attempts} tentativas</span>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {assessment.sectors && assessment.sectors.map(sector => (
                      <Badge key={sector} variant="secondary" className="text-xs">
                        {getSectorDisplayName(sector)}
                      </Badge>
                    ))}
                    <Badge variant="outline" className="text-xs">
                      Disponível
                    </Badge>
                    {isTrailFinalAssessment && (
                      <Badge variant="outline" className="text-xs text-blue-300 border-blue-500">
                        Avaliação final de trilha
                      </Badge>
                    )}
                    {linkedTrail && (
                      <Badge variant="outline" className="text-xs text-blue-200 border-blue-400">
                        {linkedTrail.name}
                      </Badge>
                    )}
                  </div>

                  {linkedTrail && (
                    <div
                      className={`text-sm rounded-lg border px-3 py-2 ${
                        linkedTrail.assessment_passed
                          ? 'border-green-500/40 bg-green-900/20 text-green-200'
                          : linkedTrail.can_take_final_assessment
                            ? 'border-blue-500/40 bg-blue-900/20 text-blue-100'
                            : 'border-amber-500/40 bg-amber-900/20 text-amber-100'
                      }`}
                    >
                      {linkedTrail.assessment_passed
                        ? 'Você já concluiu esta avaliação final.'
                        : linkedTrail.can_take_final_assessment
                          ? 'Todos os cursos da trilha foram concluídos. Avaliação liberada!'
                          : lockedReason || 'Conclua todos os cursos da trilha para liberar esta avaliação.'}
                    </div>
                  )}

                  {!linkedTrail && isTrailFinalAssessment && linkedTrailNames && (
                    <p className="text-xs text-gray-400">
                      Vinculada às trilhas: {linkedTrailNames}
                    </p>
                  )}

                  <div className="pt-4">
                    {assessmentButtonDisabled ? (
                      <Button
                        className={`w-full ${primaryButtonBase} opacity-60 cursor-not-allowed`}
                        disabled
                        data-testid={`start-assessment-${assessment.id}`}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Avaliação bloqueada
                      </Button>
                    ) : (
                      <Link to={`/assessment/${assessment.id}`}>
                        <Button className={`w-full ${primaryButtonBase}`} data-testid={`start-assessment-${assessment.id}`}>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          {startButtonLabel}
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info Section */}
        {assessments.length > 0 && (
          <div className="mt-12">
            <Card className="bg-blue-900/20 border-blue-700">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-300 mb-2">Como funcionam as avaliações?</h3>
                    <ul className="space-y-2 text-blue-200">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        Cada avaliação tem um número limitado de tentativas
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        Você precisa atingir a nota mínima para ser aprovado
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        Ao ser aprovado, você recebe um certificado digital
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        Algumas avaliações podem desbloquear novos cursos
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AssessmentsList;
